import React from 'react'
import { Selection } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'

/**
 * Clicking outside the editor in its wrapping element should focus on the editor placing the caret:
 * - at the corresponding line
 * - at the end of the line if clicked right of the editor
 * - at the start of the line if clicked left of the editor
 * - at the element or position directly below a top click
 * - at the end of the last line on a bottom click
 */
// This is being tested mainly in cypress
// See cypress/integration/editor3/proseEditorClicks/
export const focusOnEditorWhenAParentElementIsClicked =
  ({ editorView, parentElement }: { editorView?: EditorView; parentElement: Element | null }) =>
  (event: React.MouseEvent) => {
    // If you don't have an editor to focus on, don't bother
    if (editorView == null) {
      return
    }

    const somethingIsFocused = document.activeElement !== document.body
    const viewIsAlreadyFocused = editorView.hasFocus()
    // If the editor or something other than document.body is focused don't mess with it
    // (this also prevents us from messing up drag actions)
    if (somethingIsFocused || viewIsAlreadyFocused) {
      return
    }

    const target = event.target as HTMLElement | null
    const editor = parentElement?.querySelector('.ProseMirror')
    // If you don't have the areas to control the click targets, don't bother
    if (!parentElement || !editor) {
      return
    }

    // The target is already inside PM itself (comments and other plugins)
    if (editor.parentElement?.contains(target)) {
      return
    }

    // Outside what could be considered the editor (we sometimes get clicks from the Ribbon)
    if (!parentElement.contains(target)) {
      return
    }

    // Use the boundaries of PM itself
    const editorBox = editor.getBoundingClientRect()
    const { top, bottom, left, right } = editorBox
    // Without the offsets it does not work for corners
    const verticalOffset = 10
    const horizontalOffset = 1
    // Push it below the top-left corner and above the bottom-right corner
    const adjustedCoordinates = {
      left: Math.min(Math.max(event.clientX, left + horizontalOffset), right - horizontalOffset),
      top: Math.min(Math.max(event.clientY, top + verticalOffset), bottom - verticalOffset),
    }

    const { dispatch, state } = editorView

    // TODO: It works perfectly well without this
    //       But src/screens/ProseEdit/ProseEdit.test.tsx breaks and I cannot fix it
    //       My PR was https://github.com/almanaclabs/almanac-editor/pull/6344 (in case someone reindents this)
    if (event.clientY > bottom) {
      editorView.focus()
      const selection = Selection.atEnd(state.doc)
      dispatch(state.tr.setSelection(selection))
      return
    }

    const isRightSideClick = event.clientX > right
    const isLeftSideClick = event.clientX < left
    const isTopSideClick = event.clientY < top
    const isBottomSideClick = event.clientY > bottom

    // editorView.posAtCoords has a hard time dealing with tables, nested lists and other components
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    const verticalDirection = isBottomSideClick ? 'top' : isTopSideClick ? 'bottom' : undefined
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    const horizontalDirection = isRightSideClick ? 'left' : isLeftSideClick ? 'right' : undefined
    const newCoords = walkUntilEditable(editorBox, adjustedCoordinates, horizontalDirection, verticalDirection)
    if (newCoords) {
      adjustedCoordinates.left = newCoords.left
      adjustedCoordinates.top = newCoords.top
    }

    let pos = editorView.posAtCoords(adjustedCoordinates)

    // PM doesn't know what you are selecting, don't bother
    if (!pos) {
      return
    }

    // Correct PM's selection when clicking to the right of a justified paragraph with more than one line
    if (newCoords) {
      pos.pos -= newCoords.offset
    }

    // You found something, go for it
    editorView.focus()
    const selection = Selection.near(state.doc.resolve(Math.max(pos.pos, 0)), isRightSideClick ? -1 : 1)
    const thing = state.tr.setSelection(selection)
    dispatch(thing)
  }

// Make horizontal walk
// Make vertical walk
interface Coords {
  left: number
  top: number
}

const MAX_SCAN_STEPS = 1000
/**
 * PM doesn't place the caret on the right line or position in lists / tables / multiline paragraphs in Safari or Electron 87 (CI cypress).
 * This attempts to move the click coordinates until it hits an editable element, checking for the boundaries of the closest line.
 * It also recursively scans upwards / downwards depending on `verticalDirection`.
 **/
function walkUntilEditable(
  searchSpace: DOMRect,
  originalCoords: Coords,
  horizontalDirection?: 'left' | 'right',
  verticalDirection?: 'bottom' | 'top',
  maxSteps: number = MAX_SCAN_STEPS,
): (Coords & { offset: number }) | null {
  // We are already out of bounds (recursive catch)
  if (isOutOfBounds(originalCoords, searchSpace)) {
    return null
  }
  // Not enough definition
  if (!horizontalDirection && !verticalDirection) {
    return null
  }
  // Try to get an editable element
  let editableElement = editableElementsFromPoint(originalCoords)
  const adjustedCoordinates = { ...originalCoords }
  let step = 1
  while (!editableElement) {
    // We could not find any editable element in a reasonable amount of time (safety)
    if (maxSteps-- < 0) {
      return { ...originalCoords, offset: 0 }
    }

    // Move the click horizontally
    if (horizontalDirection === 'left') {
      adjustedCoordinates.left -= step
    }
    if (horizontalDirection === 'right') {
      adjustedCoordinates.left += step
    }
    // We can't deal with the topLeft/topRight diagonal, it misses content when the doc
    // - starts with a centered or right aligned title
    // - starts with nested lists
    if (horizontalDirection == null) {
      // Move the click vertically
      if (verticalDirection === 'top') {
        adjustedCoordinates.top -= step
      }
      if (verticalDirection === 'bottom') {
        adjustedCoordinates.top += step
      }
    }

    // We can miss a small paragraph by taking steps that are too large
    // We can waste a lot of time checking around an image
    step++

    // We hit the end of the editor boundaries
    if (isOutOfBounds(adjustedCoordinates, searchSpace)) {
      // We hit the other side, go one line up or down and scan again
      if (horizontalDirection) {
        // Sometimes one can click in between elements and then we have to scan the line above or the line below
        const verticalOffset = verticalDirection === 'bottom' ? 10 : -10
        adjustedCoordinates.top += verticalOffset
        const nextScanLine = { ...originalCoords, top: adjustedCoordinates.top }
        return walkUntilEditable(searchSpace, nextScanLine, horizontalDirection, verticalDirection, maxSteps)
      }

      // We found real no case could justify a vertical scan
      return null
    }

    editableElement = editableElementsFromPoint(adjustedCoordinates)
  }

  // we come from the left
  if (horizontalDirection === 'right') {
    const editableBox = editableElement.getBoundingClientRect()
    return { ...adjustedCoordinates, left: editableBox.left + 1, offset: 0 }
  }

  // we come from the right
  if (horizontalDirection === 'left') {
    // try to find the corresponding line and click closer to it
    const closestRange = getClosestRangeClientRect(editableElement, adjustedCoordinates.top)
    if (closestRange) {
      const { closestRect, isLast } = closestRange
      const rightEdgeX = closestRect.left + closestRect.width
      const rectMiddleY = closestRect.top + closestRect.height * 0.5
      // PM has the tendency to set the caret on the beginning of the next line, this corrects that
      let offset = isLast ? 0 : 1
      // Safari and Electron 87 both give us a zero width range for right or justify paragraphs
      // This fixes things for both and keeps it working on Chrome and Edge and Firefox
      if (closestRect.width === 0) {
        offset = 0
      }
      return { left: rightEdgeX - 1, top: rectMiddleY, offset }
    }

    // We could not find a line, scan again
    const verticalOffset = verticalDirection === 'bottom' ? 10 : -10
    adjustedCoordinates.top += verticalOffset
    const nextScanLine = { ...originalCoords, top: adjustedCoordinates.top }
    return walkUntilEditable(searchSpace, nextScanLine, horizontalDirection, verticalDirection, maxSteps)
  }

  return { ...adjustedCoordinates, offset: 0 }
}

function isOutOfBounds({ left, top }: Coords, bounds: DOMRect) {
  return left < bounds.left || left > bounds.right || top < bounds.top || top > bounds.bottom
}

const EDITABLE_TAG = /^P$|^H1$|^H2$|^H3$|^H4$/
function editableElementsFromPoint({ left, top }: Coords) {
  return document.elementsFromPoint(left, top).find((elem) => EDITABLE_TAG.test(elem.nodeName))
}

/**
 * This creates a range within the editable element and searches for the DOMRect of the closest line.
 * It also silences any `IndexSizeError` as we can always fallback to clicking the element itself
 **/
function getClosestRangeClientRect(
  element: Element,
  clickClientY: number,
): {
  closestRect: DOMRect
  isLast: boolean
} | null {
  try {
    // This gives us the DOMRect for each line of the element
    // This way we know where to click in list items and left aligned paragraphs
    // Cases that PM does not handle well in Electron 87 and Safari
    const range = document.createRange()
    range.setStart(element, 0)
    range.setEnd(element, element.childNodes.length)
    const rangeRects = range.getClientRects()
    let closestRect: DOMRect | undefined = undefined
    let isLast = true
    for (let i = 0; i < rangeRects.length; i++) {
      if (rangeRects[i].top > clickClientY) {
        isLast = false
        break
      }
      closestRect = rangeRects[i]
    }
    if (closestRect != null) {
      return { closestRect, isLast }
    }
    return null
  } catch (e: unknown) {
    const error = e as Error
    if (error?.message && /IndexSizeError|There is no child at offset/.test(error.message)) {
      return null
    }
    reportError(error)
    return null
  }
}
